import { Controller, useForm } from 'react-hook-form';
import { Box, Button, TextField } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { createMediaSchemaFactory } from '@/schemas/media/create-media.ts';
import { useCreateMediaMutation } from '@/state/mutations/media/use-create-media.ts';
import { MEDIA_PARENT_ENTITY, type MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import { useMediaUpload } from '@/hooks/use-media-upload.ts';
import { useMediaUploadQuotaModals } from '@/hooks/use-media-upload-quota-modals.ts';
import MediaUploadFeedback from '@/components/media/media-upload-feedback.tsx';
import InsufficientStorageContactModal from '@/components/events/detail/tabs/common/media/insufficient-storage-contact.modal.tsx';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { getOriginEventId } from '@/helpers/event.helper.ts';
import { useApproveMediaAssetMutation } from '@/state/mutations/media/use-approve-media-asset.ts';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input.tsx';
import { useCallback, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type { NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import { getMediaUploadErrorCode } from '@/helpers/media-upload-quota.helper.ts';
import { isAssetAutoApprovable } from '@/helpers/media-processing-insight.helper';
import AddMediaStorageModal from '@/components/events/detail/tabs/common/media/add-media-storage.modal.tsx';

export default function CreateMediaForm({
  parentId,
  parentType,
  placement,
  playlistScope,
  isRequested,
  uploadAfterCreate = true,
  onSuccess,
  requestSubmitLabel,
  createSubmitLabel,
}: {
  parentId: string;
  parentType: MediaParentEntity;
  placement?: { id: string; type: 'Slot' };
  playlistScope?: NonSlotFlowPlaylistScope;
  isRequested?: boolean;
  uploadAfterCreate?: boolean;
  onSuccess?: () => void;
  requestSubmitLabel?: string;
  createSubmitLabel?: string;
}) {
  const notification = useNotifications();
  const t = useTranslations();
  const { mutateAsync, isPending } = useCreateMediaMutation();
  const storeEvent = useEventStore((s) => s.event);
  const eventIdForQuota = parentType === MEDIA_PARENT_ENTITY.EVENT ? parentId : getOriginEventId(storeEvent);
  const mediaUpload = useMediaUpload();
  const quotaModals = useMediaUploadQuotaModals(eventIdForQuota);
  const { mutateAsync: approveMediaAssetMutation } = useApproveMediaAssetMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [createdMediaId, setCreatedMediaId] = useState<string | null>(null);
  const [createdTitle, setCreatedTitle] = useState<string | null>(null);

  const createMediaSchema = createMediaSchemaFactory(t);
  type CreateMedia = z.infer<typeof createMediaSchema>;
  const { control, handleSubmit, reset } = useForm<CreateMedia>({
    resolver: zodResolver(createMediaSchema),
    defaultValues: {
      title: '',
    },
  });

  const clearCreatedMedia = useCallback(() => {
    setCreatedMediaId(null);
    setCreatedTitle(null);
  }, []);

  const onSubmit = useCallback(
    async (data: { title: string }) => {
      try {
        const res = await mutateAsync({
          title: data.title,
          parentId: parentId,
          parentType: parentType.toLowerCase(),
          placement,
          playlistScope: parentType === MEDIA_PARENT_ENTITY.FLOW ? playlistScope : undefined,
          isRequested: isRequested ?? false,
        });

        notification.notifySuccess(t('media.notification.createSuccess', { title: res.title }));

        // Normal create opens upload; "signal for later" creates owned media without an asset.
        if (!isRequested && uploadAfterCreate) {
          setCreatedTitle(res.title);
          setCreatedMediaId(res.id);
          reset();
          // Small delay to ensure the form has reset and UI is ready
          setTimeout(() => {
            fileInputRef.current?.click();
          }, 100);
        } else {
          clearCreatedMedia();
          reset();
          // Close after an organizer request or a contributor's upload-later signal.
          onSuccess?.();
        }
      } catch {
        notification.notifyError(t('media.notification.createError'));
      }
    },
    [
      mutateAsync,
      reset,
      notification,
      t,
      parentId,
      parentType,
      placement,
      playlistScope,
      isRequested,
      uploadAfterCreate,
      onSuccess,
      clearCreatedMedia,
    ],
  );

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    if (files.length === 0 || !createdMediaId) {
      return;
    }

    try {
      const res = await mediaUpload.uploadFiles(createdMediaId, files);

      if (isAssetAutoApprovable(res)) {
        try {
          await approveMediaAssetMutation({
            mediaId: createdMediaId,
            assetVersion: res.version,
            isApproved: true,
            parentId,
            parentType,
          });
        } catch (approvalError) {
          console.error('Failed to auto-approve asset:', approvalError);
        }
      }

      notification.notifySuccess(t('media.notification.uploadSuccess', { name: res.name }));
      mediaUpload.reset();
      clearCreatedMedia();
      if (event.target) {
        event.target.value = '';
      }
      // Close the form after successful upload
      onSuccess?.();
    } catch (error) {
      if (!quotaModals.handleUploadError(error)) {
        const errorCode = getMediaUploadErrorCode(error);
        const errorMessage = t('media.notification.uploadError');
        const reason = errorCode ? `: ${t(`error.${errorCode}` as TranslationKey)}` : '';
        notification.notifyError(`${errorMessage}${reason}`);
      }
      mediaUpload.resetAfterUploadAttempt(error);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      handleSubmit(onSubmit)(event);
    },
    [handleSubmit, onSubmit],
  );

  const isAwaitingUpload = createdMediaId != null;
  const isBusy = isPending || mediaUpload.isBusy;

  return (
    <>
      <AddMediaStorageModal
        open={quotaModals.isAddStorageModalOpen}
        onModalClose={() => quotaModals.setIsAddStorageModalOpen(false)}
      />
      <InsufficientStorageContactModal
        open={quotaModals.isContactModalOpen}
        onModalClose={() => quotaModals.setIsContactModalOpen(false)}
      />
      <VisuallyHiddenInput
        type="file"
        multiple
        onChange={handleFileUpload}
        ref={fileInputRef}
        disabled={mediaUpload.isBusy}
      />
      <MediaUploadFeedback mediaUpload={mediaUpload} />
      <Box
        noValidate
        component="form"
        onSubmit={handleFormSubmit}
        sx={{
          mb: '1rem',
          display: 'flex',
          alignItems: 'start',
        }}
      >
        {isAwaitingUpload ? (
          <TextField
            fullWidth
            type="text"
            variant="standard"
            label={t('media.titleLabel')}
            value={createdTitle ?? ''}
            disabled
            slotProps={{
              input: { readOnly: true },
            }}
            sx={{
              '& .MuiInputBase-root': {
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
                '&:hover::before': { display: 'none' },
                '&.Mui-disabled::before': { display: 'none' },
              },
            }}
          />
        ) : (
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                autoFocus
                type="text"
                variant="standard"
                label={isRequested ? t('media.titleLabelRequested') : t('media.titleLabelAdd')}
                helperText={fieldState.error ? fieldState.error.message : null}
                error={!!fieldState.error}
              />
            )}
          />
        )}
        <Button
          type="submit"
          variant="contained"
          size="small"
          color="info"
          loading={isBusy}
          disabled={isAwaitingUpload && !isBusy}
          sx={{ m: '1rem 0 0 1rem' }}
        >
          {isRequested ? (requestSubmitLabel ?? t('common.request')) : (createSubmitLabel ?? t('common.create'))}
        </Button>
      </Box>
    </>
  );
}
